import {isFeatureEnabled} from '@github-ui/feature-flags'
import {replaceTokensDifferentially} from '@github-ui/filter-query'
import {searchUrl} from '@github-ui/issue-url-helper'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import {isLabelArchived, labelAccessibleName, RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {SoftNavOnlyLink as RouterLink} from '@github-ui/react-router'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {ActionList, Banner, Button, Checkbox, Dialog, FormControl} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import {LABELS} from './constants/labels'
import {DialogForm as EditDialog} from './DialogForm'
import {IssuesAndPullRequestsCount} from './IssuesAndPullRequestsCount'
import type {LabelNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'
import {useArchiveLabel} from './use-archive-label'
import {useDeleteLabel} from './use-delete-label'
import {useEditLabel} from './use-edit-label'

export type LabelRowFragment = Pick<LabelNode, 'id' | 'name' | 'nameHTML' | 'color' | 'description' | 'archivedAt'>

type LabelFormValues = {name: string; description?: string | null; color: string}

function labelFormValues(label: LabelRowFragment): LabelFormValues {
  return {name: label.name, description: label.description, color: label.color}
}

type LabelRowProps = {
  label: LabelRowFragment
  isActionsAvailable: boolean
  viewerCanDeleteLabels: boolean
  viewerCanUpdateLabels: boolean
  labelIds: string[]
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  isArchivingEnabled?: boolean
  setArchiveError: (message: string) => void
  onArchiveOptimisticRemove?: (labelId: string) => void
  onArchiveRollback?: (labelId: string) => void
}

export function LabelRow({
  label,
  isActionsAvailable,
  viewerCanDeleteLabels,
  viewerCanUpdateLabels,
  labelIds,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  isArchivingEnabled = false,
  setArchiveError,
  onArchiveOptimisticRemove,
  onArchiveRollback,
}: LabelRowProps) {
  const [errorsPresent, setErrorsPresent] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editSubmissionErrors, setEditSubmissionErrors] = useState('')
  // Snapshotted when the dialog opens. The dialog unmounts while closed and re-opens on a failed
  // edit, so deriving its fields from `label` would seed them from whatever the cache happens to
  // hold at that instant — which differs between the two optimistic strategies.
  const [editInitialValues, setEditInitialValues] = useState<LabelFormValues>(() => labelFormValues(label))

  const errorBannerRef = useRef<HTMLDivElement>(null)
  const listItemRef = useRef<HTMLLIElement>(null)
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const handleDelete = useDeleteLabel({
    labelId: label.id,
    isArchived: isLabelArchived(label),
    repositoryId,
    repositoryNameWithOwner,
    queryKey,
    setIsDialogOpen,
    setErrorsPresent,
  })

  const handleEdit = useEditLabel({
    labelId: label.id,
    repositoryId,
    repositoryNameWithOwner,
    queryKey,
    setEditSubmissionErrors,
    setIsEditDialogOpen,
  })

  const {mutate: updateArchiveState, isPending: isArchivePending} = useArchiveLabel({
    labelId: label.id,
    repositoryId,
    repositoryNameWithOwner,
    isArchived: isLabelArchived(label),
    queryKey,
    setArchiveError,
    onOptimisticRemove: () => onArchiveOptimisticRemove?.(label.id),
    onRollback: () => onArchiveRollback?.(label.id),
  })

  useEffect(() => {
    if (errorsPresent && errorBannerRef?.current) {
      errorBannerRef.current.focus()
    }
  }, [errorBannerRef, errorsPresent])

  const newQuery = replaceTokensDifferentially(QUERIES.open, [label.name], 'label')
  const labelLink = searchUrl({viewId: VIEW_IDS.repository, query: newQuery})

  const captureReturnFocusRef = () => {
    const trigger = listItemRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')
    if (trigger) {
      returnFocusRef.current = trigger
    }
  }

  const openEditDialog = () => {
    captureReturnFocusRef()
    setEditInitialValues(labelFormValues(label))
    setIsEditDialogOpen(true)
  }

  return (
    <LabelRowContent
      label={label}
      isActionsAvailable={isActionsAvailable}
      viewerCanDeleteLabels={viewerCanDeleteLabels}
      viewerCanUpdateLabels={viewerCanUpdateLabels}
      labelIds={labelIds}
      repositoryNameWithOwner={repositoryNameWithOwner}
      errorsPresent={errorsPresent}
      setErrorsPresent={setErrorsPresent}
      isDialogOpen={isDialogOpen}
      setIsDialogOpen={setIsDialogOpen}
      isArchiveDialogOpen={isArchiveDialogOpen}
      setIsArchiveDialogOpen={setIsArchiveDialogOpen}
      isEditDialogOpen={isEditDialogOpen}
      setIsEditDialogOpen={setIsEditDialogOpen}
      openEditDialog={openEditDialog}
      editInitialValues={editInitialValues}
      editSubmissionErrors={editSubmissionErrors}
      setEditSubmissionErrors={setEditSubmissionErrors}
      errorBanner={errorBannerRef}
      listItemRef={listItemRef}
      returnFocusRef={returnFocusRef}
      handleDelete={handleDelete}
      handleEdit={handleEdit}
      updateArchiveState={updateArchiveState}
      isArchivePending={isArchivePending}
      isArchivingEnabled={isArchivingEnabled}
      labelLink={labelLink}
      captureReturnFocusRef={captureReturnFocusRef}
    />
  )
}

type LabelRowContentProps = {
  label: LabelRowFragment
  isActionsAvailable: boolean
  viewerCanDeleteLabels: boolean
  viewerCanUpdateLabels: boolean
  labelIds: string[]
  repositoryNameWithOwner: string
  errorsPresent: boolean
  setErrorsPresent: (v: boolean) => void
  isDialogOpen: boolean
  setIsDialogOpen: (v: boolean) => void
  isArchiveDialogOpen: boolean
  setIsArchiveDialogOpen: (v: boolean) => void
  isEditDialogOpen: boolean
  setIsEditDialogOpen: (v: boolean) => void
  openEditDialog: () => void
  editInitialValues: LabelFormValues
  editSubmissionErrors: string
  setEditSubmissionErrors: (v: string) => void
  errorBanner: React.RefObject<HTMLDivElement | null>
  listItemRef: React.RefObject<HTMLLIElement | null>
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  handleDelete: () => void
  handleEdit: (input: {name: string; description: string; color: string}, setIsSubmitting: (v: boolean) => void) => void
  updateArchiveState: () => void
  isArchivePending: boolean
  isArchivingEnabled?: boolean
  labelLink: string
  captureReturnFocusRef: () => void
}

export const LABELS_ARCHIVING_INFO_STORAGE_KEY = 'gh.labels_archiving.info_dismissed'

function LabelRowContent({
  label,
  isActionsAvailable,
  viewerCanDeleteLabels,
  viewerCanUpdateLabels,
  labelIds,
  repositoryNameWithOwner,
  errorsPresent,
  setErrorsPresent,
  isDialogOpen,
  setIsDialogOpen,
  isArchiveDialogOpen,
  setIsArchiveDialogOpen,
  isEditDialogOpen,
  setIsEditDialogOpen,
  openEditDialog,
  editInitialValues,
  editSubmissionErrors,
  setEditSubmissionErrors,
  errorBanner,
  listItemRef,
  returnFocusRef,
  handleDelete,
  handleEdit,
  updateArchiveState,
  isArchivePending,
  isArchivingEnabled,
  labelLink,
  captureReturnFocusRef,
}: LabelRowContentProps) {
  const showArchiveConfirmation = isFeatureEnabled('labels_archiving_info')
  const [isArchiveConfirmationDismissed, setIsArchiveConfirmationDismissed] = useLocalStorage(
    LABELS_ARCHIVING_INFO_STORAGE_KEY,
    false,
  )

  const [isArchiveConfirmationChecked, setIsArchiveConfirmationChecked] = useState(isArchiveConfirmationDismissed)

  return (
    <ListItem
      key={label.id}
      ref={listItemRef}
      data-label-id={label.id}
      title={<ListItemTitle value={label.name} containerClassName="sr-only" />}
      role="listitem"
      secondaryActions={
        isActionsAvailable ? (
          <ListItemActionBar
            staticMenuActions={[
              ...(viewerCanUpdateLabels
                ? [
                    {
                      key: 'edit',
                      render: () => (
                        <ActionList.Item
                          onSelect={() => {
                            openEditDialog()
                          }}
                        >
                          Edit
                        </ActionList.Item>
                      ),
                    },
                  ]
                : []),
              ...(isArchivingEnabled && viewerCanUpdateLabels
                ? [
                    {
                      key: isLabelArchived(label) ? 'unarchive' : 'archive',
                      render: () => (
                        <ActionList.Item
                          disabled={isArchivePending}
                          onSelect={() => {
                            if (showArchiveConfirmation && !isArchiveConfirmationDismissed && !isLabelArchived(label)) {
                              captureReturnFocusRef()
                              setIsArchiveDialogOpen(true)
                            } else {
                              updateArchiveState()
                            }
                          }}
                        >
                          {isLabelArchived(label) ? LABELS.unarchiveButtonText : LABELS.archiveButtonText}
                        </ActionList.Item>
                      ),
                    },
                  ]
                : []),
              ...(viewerCanDeleteLabels
                ? [
                    {
                      key: 'delete',
                      render: () => (
                        <>
                          <ActionList.Divider />
                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              captureReturnFocusRef()
                              setIsDialogOpen(true)
                            }}
                          >
                            {LABELS.deleteButtonText}
                          </ActionList.Item>
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        ) : undefined
      }
    >
      <ListItemMainContent>
        <ListItemDescription className={clsx(styles.labelRowDescription, !isActionsAvailable && styles.noActionBar)}>
          <ListItemDescriptionItem>
            <RouterLink to={labelLink} aria-label={labelAccessibleName(label)}>
              <RepositoryLabelToken label={label} interactive key={0} />
            </RouterLink>{' '}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className={styles.labelRowDescriptionItemDescription}>
            {label.description}
          </ListItemDescriptionItem>
          <IssuesAndPullRequestsCount
            repositoryNameWithOwner={repositoryNameWithOwner}
            labelName={label.name}
            labelId={label.id}
            labelIds={labelIds}
          />
        </ListItemDescription>
      </ListItemMainContent>
      {viewerCanDeleteLabels && isDialogOpen && (
        <Dialog
          title={LABELS.labelDeleteDialogTitle}
          onClose={() => {
            setErrorsPresent(false)
            setIsDialogOpen(false)
          }}
          returnFocusRef={returnFocusRef}
        >
          <Dialog.Body>
            {errorsPresent && (
              <Banner
                ref={errorBanner}
                className="tmp-mb-3"
                title="Error"
                description={<AriaAlert>{LABELS.deleteLabelError}</AriaAlert>}
                variant="critical"
                role="alert"
              />
            )}
            <p>{LABELS.deleteLabelWarningPermanent}</p>
            <p>{LABELS.deleteLabelAssociatedIssuesNote}</p>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              onClick={() => {
                setErrorsPresent(false)
                setIsDialogOpen(false)
              }}
            >
              {LABELS.cancelButtonText}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              {LABELS.deleteLabelButtonText}
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
      {showArchiveConfirmation && isArchiveDialogOpen && !isArchiveConfirmationDismissed && (
        <Dialog
          title={LABELS.archiveLabel}
          subtitle={<span className="sr-only">{LABELS.archiveDialogBody(label.nameHTML)}</span>}
          role="alertdialog"
          renderFooter={() => (
            <Dialog.Footer className={styles.archiveDialogFooter}>
              <FormControl className={styles.archiveDialogDismissal}>
                <Checkbox
                  checked={isArchiveConfirmationChecked}
                  onChange={() => {
                    setIsArchiveConfirmationChecked(prev => !prev)
                  }}
                />
                <FormControl.Label>{LABELS.archiveLabelConfirmationDismissed}</FormControl.Label>
              </FormControl>
              <div className={styles.archiveDialogActions}>
                <Button
                  onClick={() => {
                    setIsArchiveDialogOpen(false)
                    setIsArchiveConfirmationChecked(false)
                    requestAnimationFrame(() => returnFocusRef.current?.focus({preventScroll: true}))
                  }}
                >
                  {LABELS.cancelButtonText}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsArchiveDialogOpen(false)
                    if (isArchiveConfirmationChecked) {
                      setIsArchiveConfirmationDismissed(true)
                    }
                    if (!isLabelArchived(label)) {
                      updateArchiveState()
                    }
                  }}
                >
                  {LABELS.archiveButtonText}
                </Button>
              </div>
            </Dialog.Footer>
          )}
          onClose={gesture => {
            if (gesture === 'escape' || gesture === 'close-button') {
              setIsArchiveDialogOpen(false)
              setIsArchiveConfirmationChecked(false)
              requestAnimationFrame(() => returnFocusRef.current?.focus({preventScroll: true}))
            }
          }}
        >
          {LABELS.archiveDialogBody(label.nameHTML)}
        </Dialog>
      )}
      {isEditDialogOpen && (
        <EditDialog
          onDialogClose={() => {
            setEditSubmissionErrors('')
            setIsEditDialogOpen(false)
          }}
          formTitle={LABELS.editLabel}
          descriptionPlaceholder={LABELS.labelDescriptionPlaceholder}
          submissionErrors={editSubmissionErrors}
          submitButtonText={LABELS.saveChanges}
          onDialogSubmit={handleEdit}
          returnFocusRef={returnFocusRef}
          initialValues={editInitialValues}
        />
      )}
    </ListItem>
  )
}
